import { search } from "@/api";
import EmptyBox from "@/component/loader/EmptyBox";
import Loader from "@/component/loader/Loader";
import { ISearch, Product } from "@/lib/interfaces/ISearch";
import { inter, poppins } from "@/styles/font";
import { Badge, Box, Flex, Grid, GridItem, Stack, Text } from "@chakra-ui/react"
import { Eye } from "@phosphor-icons/react";
import { AxiosError } from "axios";
import { NextSeo } from "next-seo";
import Image from "next/image";
import { useRouter } from "next/router";
import { useQuery } from "react-query";

export const ShopItemProduct = ({ product }: { product: Product }) => {
    const router = useRouter();
    return (
        <GridItem w="100%" minWidth="100%">
            <Flex
                flexDirection={"column"}
                rounded={"md"}
                _hover={{ cursor: "pointer", transform: "scale(1.02)" }}
                transition={"all .3s"}
                onClick={() =>
                    router.push({
                        pathname:
                            `/catalog/[categoryId]/[marketId]/shops/[shopId]/[productId]`,
                        query: {
                            categoryId: product.subcategory[0].category._id,
                            marketId: product.market._id, shopId: product.store._id,
                            productId: product._id, category: product.subcategory[0].category.title,
                            market: product.market.title,
                            shop: product.store.name,
                            subCatId: product.subcategory[0]._id,
                            stateId: product.market.state
                        }
                    })
                }
            >
                <Box position={"relative"}>
                    <Box position={"relative"} overflow={'hidden'} shadow={'sm'} rounded={'lg'} boxSize={{ base: "140px", lg: "150px" }}>
                        <Image src={product.image[0]} fill alt={product.name} />
                        <Badge
                            textAlign={"center"}
                            right={2}
                            bottom={2}
                            position={"absolute"}
                            rounded={"xl"}
                            backgroundColor={"martlineorange.100"}
                            color={"martlineorange.500"}
                            fontSize={"14px"}
                            fontWeight={"semibold"}
                            className={inter.className}
                        >
                            <Stack spacing={1} direction={"row"} alignItems={"center"}>
                                <Eye
                                    size={16}
                                    color={"hsla(25, 100%, 54%, 1)"}
                                    weight={"bold"}
                                />
                                <Text
                                    fontWeight={"normal"}
                                    className={inter.className}
                                    color={"hsla(25, 100%, 54%, 1)"}
                                >
                                    {product.views}
                                </Text>
                            </Stack>
                        </Badge>
                    </Box>
                </Box>
                <Text
                    fontSize={"12px"}
                    fontWeight={"normal"}
                    mt={"6px"}
                    color={"martlinegray.900"}
                    className={inter.className}
                >
                    {product.caption}
                </Text>
                <Text
                    fontSize={"14px"}
                    fontWeight={"normal"}
                    color={"martlinegray.900"}
                    className={poppins.className}
                >
                    {`₦${new Intl.NumberFormat().format(Number(product.price))}`}
                </Text>
            </Flex>
        </GridItem>
    );
};

const Search = () => {
    const router = useRouter();
    const { query, id } = router.query;

    const { isLoading, isSuccess, isError, error, data }
        = useQuery<ISearch, AxiosError>(["search", query?.toString()],
            async () => search(query?.toString(), id?.toString()), {
            useErrorBoundary: true,
            enabled: !!query,
        });

    return (
        <Flex minHeight={"80vh"} flexDirection={"column"} py={{ base: 4, lg: 8 }} mx={{ base: 5, lg: 0 }}>
            <NextSeo
                title={'Search | Martlines'}
                description={''}
            />
            <Text className={poppins.className}>
                Search results for <Text fontWeight={'semibold'} fontStyle={'italic'} as={'span'}>{`${query?.toString()}`}</Text>
            </Text>
            {isLoading && <Loader />}
            {isSuccess && data.product.length === 0 && <EmptyBox message={'No product found'} />}
            {isSuccess &&
                <Grid
                    gridTemplateColumns={{
                        base: "repeat(2, 1fr)",
                        lg: "repeat(6, 1fr)",
                    }}
                    mt={{ base: 4, lg: 6 }}
                    gridAutoColumns={'max-content'}
                    rowGap={{ base: 3, lg: 6 }}
                    columnGap={3}
                    alignItems={"stretch"}
                    placeItems={'center'}
                >
                    {
                        data.product.map((item) =>
                            <ShopItemProduct key={item._id} product={item} />)
                    }
                </Grid>
            }
        </Flex>
    )
}

export default Search;